import { Avatar, Box, IconButton, Stack, Typography } from '@mui/material';

import { PerformerFragment } from 'types/api/dato-operations.types';

import DatoImage from 'components/@common/DatoImage';
import LinkUnstyled from 'components/@common/LinkUnstyled';
import { formatInitialsName } from 'components/@events/LineupItemList/utils';
import Icon from 'components/@icons/Icon';

import { mapSoundPlayerLinks } from './utils';

interface Props {
  performer: PerformerFragment;
  isOpen?: boolean;
}

const LineupItem = ({ performer, isOpen }: Props) => {
  const lookupIconsPerformers = {
    soundcloud: performer.soundcloud,
    anghami: performer.anghami,
    mixcloud: performer.mixcloud,
    spotify: performer.spotify,
    youtube: performer.youtube,
  };

  const soundPlayers = mapSoundPlayerLinks(lookupIconsPerformers);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          flex: 1,
          flexWrap: 'wrap',
          alignItems: 'center',
          overflow: 'hidden',
          position: 'relative',
          border: '1px solid',
          borderColor: 'grey.700',
          borderRadius: 4,
          p: { xs: 1, md: 2 },
          rowGap: 0,
          columnGap: 2,
        }}
      >
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            gap: 2,
          }}
        >
          {performer?.background?.url ? (
            <Avatar
              key={performer.id}
              sx={{
                width: { xs: isOpen ? '58px' : '40px', md: '80px' },
                height: { xs: isOpen ? '58px' : '40px', md: '80px' },
              }}
            >
              <DatoImage
                data={performer.background}
                style={{ objectFit: 'cover' }}
                fill
                sizes="(max-width: 900px) 64px, 128px"
              />
            </Avatar>
          ) : (
            <Avatar
              key={performer.id}
              sx={{
                width: { xs: isOpen ? '58px' : '40px', md: '80px' },
                height: { xs: isOpen ? '58px' : '40px', md: '80px' },
                bgcolor: 'grey.900',
              }}
            >
              {isOpen ? (
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 'bold',
                    color: 'text.primary',
                    mb: -0.5,
                  }}
                >
                  {formatInitialsName(performer.name)}
                </Typography>
              ) : (
                <Typography
                  variant="small"
                  sx={{
                    fontWeight: 'bold',
                    color: 'text.primary',
                  }}
                >
                  {formatInitialsName(performer.name)}
                </Typography>
              )}
            </Avatar>
          )}

          <Typography
            variant="h7"
            sx={{
              justifyContent: 'flex-start',
            }}
          >
            {performer.name}
          </Typography>
        </Stack>

        {!!soundPlayers?.length && (
          <Stack
            direction="row"
            sx={{
              flex: 1,
              gap: 2,
              p: 1,
              justifyContent: 'flex-end',
            }}
          >
            {soundPlayers.map(({ key, iconName, href }) => (
              <IconButton
                key={key}
                component={LinkUnstyled}
                variant="standard"
                href={href}
                target="_blank"
                sx={{
                  p: 0,
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                <Icon name={iconName} fontSize={24} />
              </IconButton>
            ))}
          </Stack>
        )}
      </Box>
    </>
  );
};

export default LineupItem;
